import { noop } from 'lodash';
import * as React from 'react';
import { ReactNode } from 'react';
import Transition, { ENTERED, EXITED } from 'react-transition-group/Transition';

import { FreelancerInfoPanelContainer } from '../../containers/FreelancerInfoPanelContainer/FreelancerInfoPanelContainer';
import { Bem } from '../../service/Bem/Bem';

import './CommonGridScreen.css';

export interface CommonGridScreenState {
    isHeaderVisible: boolean;
    isGridAnimating: boolean;
}

export interface CommonGridScreenProps {
    header: JSX.Element | null;
    body: JSX.Element;
    footer?: JSX.Element;
    isFreelancerPage?: boolean;
    disabledAnimation?: boolean;
}

export interface CommonGridScreenContextProps {
    onGridScroll: (
        prevScrollPosition: number,
        currentScrollPosition: number
    ) => void;
    onTableHeightChange: (newHeight: number) => void;
    isHorizontalScrollVisible: boolean;
    gridAdditionalHeight: number;
    isGridTotalBoxNotHiddenByScroll: boolean;
    isGridAnimating: boolean;
}

export const CommonGridScreenContext = React.createContext<
    CommonGridScreenContextProps
>({
    onGridScroll: noop,
    onTableHeightChange: noop,
    isHorizontalScrollVisible: false,
    gridAdditionalHeight: 0,
    isGridTotalBoxNotHiddenByScroll: true,
    isGridAnimating: true
});

export class CommonGridScreen extends React.Component<
    CommonGridScreenProps,
    CommonGridScreenState
> {
    private headerContent: React.ReactNode;
    private bodyContent: React.ReactNode;

    constructor(props: CommonGridScreenProps) {
        super(props);

        this.state = {
            isHeaderVisible: true,
            isGridAnimating: true
        };
    }

    render() {
        const { header, body, footer, isFreelancerPage = false } = this.props;
        const { isHeaderVisible, isGridAnimating } = this.state;
        const headerHeight = isHeaderVisible
            ? 0
            : (this.headerContent as Element).clientHeight;

        return (
            <Transition in={isHeaderVisible} timeout={200}>
                {(state: string) => {
                    const isHorizontalScrollVisible =
                        state === EXITED || state === ENTERED;
                    return (
                        <CommonGridScreenContext.Provider
                            value={{
                                onTableHeightChange: this.onTableHeightChange,
                                onGridScroll: this.onGridScroll,
                                isHorizontalScrollVisible: isHorizontalScrollVisible,
                                gridAdditionalHeight: headerHeight,
                                isGridTotalBoxNotHiddenByScroll: isHeaderVisible,
                                isGridAnimating: isGridAnimating
                            }}
                        >
                            <div
                                className={Bem.cls({
                                    block: 'common-grid-screen'
                                })}
                            >
                                <div ref={this.getHeaderRef}>
                                    <FreelancerInfoPanelContainer
                                        isFreelancerPage={isFreelancerPage}
                                    />

                                    <div
                                        className={Bem.cls(
                                            'common-grid-screen',
                                            'header'
                                        )}
                                    >
                                        {header}
                                    </div>
                                </div>
                                <div
                                    className={Bem.cls(
                                        'common-grid-screen',
                                        'body',
                                        {
                                            animate: isGridAnimating
                                        }
                                    )}
                                    ref={this.getBodyRef}
                                    style={
                                        headerHeight
                                            ? {
                                                  transform: `translate3d(0, -${headerHeight}px, 0)`
                                              }
                                            : {} // transform убирается специально из-за ошибки в safari, см. DIRECT-90195
                                    }
                                >
                                    {body}
                                </div>
                                {footer}
                            </div>
                        </CommonGridScreenContext.Provider>
                    );
                }}
            </Transition>
        );
    }

    private getHeaderRef = (ref: ReactNode) => (this.headerContent = ref);
    private getBodyRef = (ref: ReactNode) => (this.bodyContent = ref);

    private onGridScroll = (
        prevScrollPosition: number,
        currentScrollPosition: number
    ) => {
        if (prevScrollPosition !== 0 && currentScrollPosition === 0) {
            this.setState({ isHeaderVisible: true });
        }

        if (prevScrollPosition === 0 && currentScrollPosition !== 0) {
            this.setState({ isHeaderVisible: false });
        }
    };

    // Функция, отвечающая за отображение шапки и анимации при изменении высоты грида
    // Возможны следующие случаи:
    // 1.Если после смены высоты грида следует пропадажа скролл -> отключаем плавную анимацию и отображаем шапку
    // 2.Если после смены высоты грида следует скролл остается -> включаем плавную анимацию, положение шапки оставляем прежнее.
    private onTableHeightChange = (newHeight: number) => {
        if (this.props.disabledAnimation) {
            return;
        }

        const { isHeaderVisible, isGridAnimating } = this.state;
        const headerContent = this.headerContent as Element;
        const bodyContent = this.bodyContent as Element;

        if (headerContent && bodyContent) {
            const bodyHeight = bodyContent.clientHeight;
            // Необходимо установить на сколько увеличился грид
            const headerHeight = isHeaderVisible
                ? 0
                : headerContent.clientHeight;
            // Вычитаем размер, на который увеличился грид и сравниваем с контейнером
            // Поумолчанию грид растянут на всю страницу, поэтому возможны случаи:
            // Высота грида === высота контейнера
            if (newHeight - headerHeight <= bodyHeight) {
                this.setState({
                    isGridAnimating: false,
                    isHeaderVisible: true
                });
                // Высота грида > высота контейнера(появляется скролл)
            } else if (!isGridAnimating) {
                this.setState({
                    isGridAnimating: true
                });
            }
        }
    };
}
